/**
 * 統一錯誤處理機制
 * 提供一致的錯誤顯示和處理方式
 */

import { toastController, alertController } from '@ionic/vue'
import type { AxiosError } from 'axios'

/**
 * 錯誤類型
 */
export type ErrorType = 'network' | 'auth' | 'validation' | 'server' | 'unknown'

/**
 * 錯誤信息接口
 */
export interface ErrorInfo {
  type: ErrorType
  message: string
  details?: string
  originalError?: Error | AxiosError
}

/**
 * HTTP 狀態碼對應的錯誤信息
 */
const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: '請求參數錯誤',
  401: '登入已過期，請重新登入',
  403: '沒有權限執行此操作',
  404: '請求的資源不存在',
  408: '請求超時，請稍後重試',
  422: '資料驗證失敗',
  429: '請求過於頻繁，請稍後重試',
  500: '伺服器錯誤，請稍後重試',
  502: '網關錯誤',
  503: '服務暫時不可用',
  504: '網關超時'
}

/**
 * 解析 Axios 錯誤
 */
export function parseAxiosError(error: AxiosError): ErrorInfo {
  if (error.response) {
    const status = error.response.status
    const message = HTTP_ERROR_MESSAGES[status] || `請求失敗 (${status})`

    let type: ErrorType = 'server'
    if (status === 401) type = 'auth'
    else if (status >= 400 && status < 500) type = 'validation'

    return {
      type,
      message,
      details: error.message,
      originalError: error
    }
  } else if (error.request) {
    return {
      type: 'network',
      message: '網路連線失敗，請檢查網路設定',
      details: error.message,
      originalError: error
    }
  } else {
    return {
      type: 'unknown',
      message: '發生未知錯誤',
      details: error.message,
      originalError: error
    }
  }
}

/**
 * 解析一般錯誤
 */
export function parseError(error: unknown): ErrorInfo {
  if (error instanceof Error) {
    // 檢查是否為 Axios 錯誤
    if ('isAxiosError' in error && (error as AxiosError).isAxiosError) {
      return parseAxiosError(error as AxiosError)
    }

    return {
      type: 'unknown',
      message: error.message || '發生未知錯誤',
      originalError: error
    }
  }

  if (typeof error === 'string') {
    return {
      type: 'unknown',
      message: error
    }
  }

  return {
    type: 'unknown',
    message: '發生未知錯誤'
  }
}

/**
 * 顯示 Toast 錯誤訊息
 */
export async function showErrorToast(
  message: string,
  duration: number = 3000
): Promise<void> {
  const toast = await toastController.create({
    message,
    duration,
    position: 'top',
    color: 'danger',
    buttons: [
      {
        text: '關閉',
        role: 'cancel'
      }
    ]
  })
  await toast.present()
}

/**
 * 顯示成功 Toast 訊息
 */
export async function showSuccessToast(
  message: string,
  duration: number = 2000
): Promise<void> {
  const toast = await toastController.create({
    message,
    duration,
    position: 'top',
    color: 'success'
  })
  await toast.present()
}

/**
 * 顯示警告 Toast 訊息
 */
export async function showWarningToast(
  message: string,
  duration: number = 3000
): Promise<void> {
  const toast = await toastController.create({
    message,
    duration,
    position: 'top',
    color: 'warning'
  })
  await toast.present()
}

/**
 * 顯示錯誤 Alert 對話框
 */
export async function showErrorAlert(
  message: string,
  header: string = '錯誤'
): Promise<void> {
  const alert = await alertController.create({
    header,
    message,
    buttons: ['確定']
  })
  await alert.present()
}

/**
 * 顯示確認對話框
 */
export async function showConfirmAlert(
  message: string,
  header: string = '確認'
): Promise<boolean> {
  return new Promise(async (resolve) => {
    const alert = await alertController.create({
      header,
      message,
      buttons: [
        {
          text: '取消',
          role: 'cancel',
          handler: () => resolve(false)
        },
        {
          text: '確定',
          handler: () => resolve(true)
        }
      ]
    })
    await alert.present()
  })
}

/**
 * 統一錯誤處理函數
 * 根據錯誤類型自動選擇顯示方式
 */
export async function handleError(
  error: unknown,
  options: {
    showToast?: boolean
    showAlert?: boolean
    logToConsole?: boolean
  } = {}
): Promise<ErrorInfo> {
  const { showToast = true, showAlert = false, logToConsole = true } = options

  const errorInfo = parseError(error)

  // 記錄到控制台
  if (logToConsole) {
    console.error('[Error Handler]', errorInfo.type, errorInfo.message, errorInfo.originalError)
  }

  // 顯示錯誤訊息
  if (showAlert) {
    await showErrorAlert(errorInfo.message)
  } else if (showToast) {
    await showErrorToast(errorInfo.message)
  }

  return errorInfo
}

/**
 * 包裝異步函數，自動處理錯誤
 */
export function withErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  options: {
    showToast?: boolean
    showAlert?: boolean
    rethrow?: boolean
  } = {}
): T {
  const { showToast = true, showAlert = false, rethrow = false } = options

  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args)
    } catch (error) {
      await handleError(error, { showToast, showAlert })
      if (rethrow) throw error
      return undefined
    }
  }) as T
}

export default {
  parseError,
  parseAxiosError,
  handleError,
  showErrorToast,
  showSuccessToast,
  showWarningToast,
  showErrorAlert,
  showConfirmAlert,
  withErrorHandling
}
